/**
 * ProspectPanel - Minimal Analyst Panel (Concept A)
 * A fixed right-side panel for managing prospects in DVA Pro Journey
 * 
 * Features:
 * - Drag & drop reordering
 * - Collapsible state
 * - Action buttons (Profile, ROI, Plan)
 * - Remove functionality
 * - Status indicators
 */

import { useState, useEffect } from 'react';
import { GripVertical, X, ChevronRight, ChevronLeft, FileText, DollarSign, Map } from 'lucide-react';

export type ProspectStatus = 'Prospect' | 'Qualified' | 'Roadmapping' | 'Approval' | 'Execution';

export interface Prospect {
  id: string;
  abbreviation: string;
  fullName: string;
  status: ProspectStatus;
}

export interface ProspectPanelProps {
  prospects: Prospect[];
  onReorder?: (reorderedProspects: Prospect[]) => void;
  onRemove?: (prospectId: string) => void;
  onProfileClick?: (prospectId: string) => void;
  onRoiClick?: (prospectId: string) => void;
  onPlanClick?: (prospectId: string) => void;
  defaultCollapsed?: boolean;
}

const getStatusColorClass = (status: ProspectStatus): string => {
  switch (status) {
    case 'Prospect':
      return 'text-slate-600';
    case 'Qualified':
      return 'text-indigo-600';
    case 'Roadmapping':
      return 'text-purple-600';
    case 'Approval':
      return 'text-amber-600';
    case 'Execution':
      return 'text-emerald-600';
    default:
      return 'text-slate-600';
  }
};

export default function ProspectPanel({
  prospects: initialProspects,
  onReorder,
  onRemove,
  onProfileClick,
  onRoiClick,
  onPlanClick,
  defaultCollapsed = false,
}: ProspectPanelProps) {
  const [prospects, setProspects] = useState<Prospect[]>(initialProspects);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  // Sync internal state when props change
  useEffect(() => {
    setProspects(initialProspects);
  }, [initialProspects]);

  const handleDragStart = (id: string) => {
    setDraggedItem(id);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;

    const draggedIndex = prospects.findIndex(p => p.id === draggedItem);
    const targetIndex = prospects.findIndex(p => p.id === targetId);

    const newProspects = [...prospects];
    const [removed] = newProspects.splice(draggedIndex, 1);
    newProspects.splice(targetIndex, 0, removed);

    setProspects(newProspects);
    onReorder?.(newProspects);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleRemove = (id: string) => {
    const newProspects = prospects.filter(p => p.id !== id);
    setProspects(newProspects);
    onRemove?.(id);
  };

  const showPlan = (status: string) => {
    return ['Qualified', 'Roadmapping', 'Approval', 'Execution'].includes(status);
  };

  if (isCollapsed) {
    return (
      <div className="w-12 bg-white border-l border-gray-200 flex flex-col items-center py-4 gap-4">
        <button
          onClick={() => setIsCollapsed(false)}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
          title="Expand Prospect List"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#18365E] text-white text-xs font-semibold">
          {prospects.length}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#18365E]">Prospect List</h3>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {prospects.length}
          </span>
        </div>
        <button
          onClick={() => setIsCollapsed(true)}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Collapse"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable Items */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {prospects.length === 0 ? (
          <div className="text-xs text-gray-500 py-8 text-center">
            No prospects selected yet.<br />
            Add prospects from the portfolio matrix.
          </div>
        ) : (
          prospects.map((prospect) => {
            const statusColorClass = getStatusColorClass(prospect.status);
            
            return (
              <div
                key={prospect.id}
                draggable
                onDragStart={() => handleDragStart(prospect.id)}
                onDragOver={(e) => handleDragOver(e, prospect.id)}
                onDragEnd={handleDragEnd}
                className={`group p-3 border-b border-gray-200 hover:bg-gray-50 transition-all cursor-move ${
                  draggedItem === prospect.id ? 'opacity-50' : ''
                }`}
              >
                {/* Row Layout */}
                <div className="flex items-start gap-2">
                  {/* Drag Handle */}
                  <div className="mt-0.5 text-gray-300 group-hover:text-gray-400 transition-colors">
                    <GripVertical className="w-4 h-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Abbreviation + Name */}
                    <div className="flex items-center gap-2 mb-1">
                      {/* Show abbreviation pill if we have a real abbreviation that's different from name */}
                      {prospect.abbreviation && 
                       prospect.abbreviation.trim() !== '' && 
                       prospect.abbreviation !== prospect.fullName ? (
                        <>
                          <span className="text-xs font-semibold text-[#18365E] bg-gray-100 px-2 py-0.5 rounded">
                            {prospect.abbreviation}
                          </span>
                          <span className="text-xs font-medium text-gray-900 truncate" title={prospect.fullName}>
                            {prospect.fullName}
                          </span>
                        </>
                      ) : (
                        /* If no separate abbreviation, show name in pill */
                        <span className="text-xs font-semibold text-[#18365E] bg-gray-100 px-2 py-0.5 rounded truncate" title={prospect.fullName}>
                          {prospect.fullName}
                        </span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="text-xs text-gray-600 mb-2">
                      Status: <span className={`font-medium ${statusColorClass}`}>{prospect.status}</span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => onProfileClick?.(prospect.id)}
                        className="text-xs font-medium text-gray-700 hover:text-[#18365E] hover:bg-gray-100 bg-white border border-gray-300 px-2.5 py-1.5 rounded-md transition-all flex items-center gap-1"
                      >
                        <FileText className="w-3 h-3" />
                        Profile
                      </button>
                      <button 
                        onClick={() => onRoiClick?.(prospect.id)}
                        className="text-xs font-medium text-gray-700 hover:text-[#18365E] hover:bg-gray-100 bg-white border border-gray-300 px-2.5 py-1.5 rounded-md transition-all flex items-center gap-1"
                      >
                        <DollarSign className="w-3 h-3" />
                        ROI
                      </button>
                      {showPlan(prospect.status) && (
                        <button 
                          onClick={() => onPlanClick?.(prospect.id)}
                          className="text-xs font-medium text-gray-700 hover:text-[#18365E] hover:bg-gray-100 bg-white border border-gray-300 px-2.5 py-1.5 rounded-md transition-all flex items-center gap-1"
                        >
                          <Map className="w-3 h-3" />
                          Plan
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemove(prospect.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-600">
          <div className="flex items-center justify-between mb-1">
            <span>Total Prospects:</span>
            <span className="font-semibold text-gray-900">{prospects.length}</span>
          </div>
          <p className="text-gray-500 mt-2">Drag to reorder by priority</p>
        </div>
      </div>
    </div>
  );
}

